import React, { useEffect, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import { DndItemTypes } from "../../constants/dndItemTypes";

import Activity from "../activities/Activity";
import ActivityCreator from "../activities/ActivityCreator";
import StepInfo from "./StepInfo";
import StepForm from "./StepForm";
import StepLockButton from "./StepLockButton";

import IconButton from "../utilities/IconButton";
import ProcedureCard from "../utilities/ProcedureCard";

import AutomationControlDecorator from "../../decorators/AutomationControlDecorator";

import { StepSelectOptions } from "../../contexts/StepSelectOptions";
import { StepLock } from "../../contexts/StepLock";

import { useReactionsFetcher } from "../../fetchers/ReactionsFetcher";

const StepColumCard = ({ processStep, reactionProcess, previousStep, onCloseForm }) => {
  const isInitialised = !!processStep;
  const [showForm, setShowForm] = useState(!isInitialised);
  const [columnWidth, setColumnWidth] = useState(360);
  const [activeResize, setActiveResize] = useState();
  const cardTitle = isInitialised ? processStep.label : "New Step";
  const api = useReactionsFetcher();
  const isLocked = !!processStep?.locked;

  useEffect(() => {
    if (!activeResize) return undefined;

    const resizeColumn = (event) => {
      const direction = activeResize.edge === "right" ? 1 : -1;
      const proposedWidth = activeResize.width
        + ((event.clientX - activeResize.clientX) * direction);
      const maximumWidth = Math.max(280, window.innerWidth * 0.8);
      setColumnWidth(Math.min(maximumWidth, Math.max(280, proposedWidth)));
    };
    const stopResizing = () => setActiveResize(undefined);

    window.addEventListener("pointermove", resizeColumn);
    window.addEventListener("pointerup", stopResizing);
    return () => {
      window.removeEventListener("pointermove", resizeColumn);
      window.removeEventListener("pointerup", stopResizing);
    };
  }, [activeResize]);

  const startResizing = (edge) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    setActiveResize({ edge, clientX: event.clientX, width: columnWidth });
  };

  const currentAutomationStatus = AutomationControlDecorator.automationStatusByName(processStep?.automation_control?.status) || AutomationControlDecorator.defaultStepAutomationStatus

  const displayMode = () => {
    return showForm ? "form" : "info";
  };

  const confirmDeleteStep = () => {
    window.confirm(
      "Deleting the ProcessStep will irreversably delete this Step and all associated Activities. " +
      "This can not be undone. Are you sure?"
    ) && deleteStep();
  };

  const deleteStep = () => api.deleteProcessStep(processStep.id);

  const closeForm = () => isInitialised ? setShowForm(false) : onCloseForm()

  const handleSave = (reactionProcessStep) => {
    isInitialised ?
      api.updateProcessStep(reactionProcessStep)
      :
      api.createProcessStep(reactionProcess.id, reactionProcessStep)
    closeForm();
  };

  const toggleForm = () => setShowForm(!showForm);

  /* React-DnD drag source and drop target */
  const [{ isDragging }, dragRef, previewRef] = useDrag(
    () => ({
      type: DndItemTypes.STEP,
      item: { processStep: processStep },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
        canDrag: monitor.canDrag(),
      }),
      canDrag: () => processStep && !isLocked,
    }),
    [processStep]
  );

  const [{ isOver }, dropRef] = useDrop(
    () => ({
      accept: DndItemTypes.STEP,
      drop: (monitor) => dropItem(monitor, processStep),
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
      canDrop: () => processStep && !isLocked,
    }),
    [processStep]
  );

  const dropItem = (monitor, processStep) => {
    api.updateProcessStepPosition(monitor.processStep.id, processStep.position);
  };

  const renderActivities = () => {
    return processStep.activities.map((activity) => (
      <Activity
        key={activity.id}
        activity={activity}
        processStep={processStep}
      />
    ));
  };

  const renderTitleBar = (title) => {
    return (
      <div className="d-md-flex gap-2">
        <div title={currentAutomationStatus.tooltip}>
          <IconButton disabled
            positive={false}
            icon={currentAutomationStatus.icon}
            color={currentAutomationStatus.color} />
        </div>
        {title}
      </div>
    )

  }

  return (
    <StepSelectOptions.Provider value={processStep?.select_options}>
      <div ref={dropRef} style={{ opacity: isOver ? 0.5 : 1 }}>
        <div
          ref={previewRef}
          className={
            "draggable-element" +
            (isDragging ? " draggable-element--dragging" : "")
          }
        >
          <StepLock.Provider value={isLocked}>
            <div
              className="step-column-card__resize-container"
              style={{ width: columnWidth }}
            >
              <div
                aria-label="Resize step column from left edge"
                aria-orientation="vertical"
                className="step-column-card__resize-handle step-column-card__resize-handle--left"
                onPointerDown={startResizing("left")}
                role="separator"
              />
              <ProcedureCard
                title={renderTitleBar(cardTitle)}
                type="step"
                showEditBtn={!showForm && !isLocked}
                showMoveBtn={!showForm && !isLocked}
                showDeleteBtn={!showForm && !isLocked}
                showCancelBtn={showForm && !isLocked}
                onDelete={confirmDeleteStep}
                onEdit={toggleForm}
                onCancel={closeForm}
                displayMode={displayMode()}
                dragRef={dragRef}
                customClass="procedure-card--column step-column-card"
              >
              <ProcedureCard.Info>
                <StepInfo processStep={processStep} />
              </ProcedureCard.Info>
              <ProcedureCard.Form>
                <StepForm
                  processStep={processStep}
                  reactionProcess={reactionProcess}
                  previousStep={previousStep}
                  nameSuggestionOptions={
                    reactionProcess.select_options.step_name_suggestions
                  }
                  initialSampleVessel={reactionProcess.reaction_process_vessel}
                  onSave={handleSave}
                  onCancel={closeForm}
                />
              </ProcedureCard.Form>
              {isInitialised && (
                <ProcedureCard.Details>
                  <div className="step-column-card__condition-container">
                    {renderActivities()}
                    {!isLocked && (
                      <ActivityCreator
                        processStep={processStep}
                        preconditions={processStep.final_conditions}
                        insertNewBeforeIndex={processStep.activities.length}
                        onClose={(e => { })}
                      />
                    )}
                  </div>
                </ProcedureCard.Details>
              )}
              {isInitialised && !showForm && (
                <ProcedureCard.ExtraButtons>
                  <div className="d-md-flex gap-2">
                    <StepLockButton
                      stepId={processStep?.id}
                      locked={isLocked}
                    />
                  </div>
                </ProcedureCard.ExtraButtons>
              )}
              </ProcedureCard>
              <div
                aria-label="Resize step column from right edge"
                aria-orientation="vertical"
                className="step-column-card__resize-handle step-column-card__resize-handle--right"
                onPointerDown={startResizing("right")}
                role="separator"
              />
            </div>
          </StepLock.Provider>
        </div>
      </div>
    </StepSelectOptions.Provider>
  );
};

export default StepColumCard;
